import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, readJson } from "../../lib/api";
import { useToast } from "../Toast";
import { Modal } from "../Modal";
import type { WorkType } from "../../lib/types";
import { SitesSection } from "./SitesSection";
import { useUnits } from "../../hooks/useUnits";
import type { UnitSystem } from "../../lib/units";

// Per-org work types: the styles this room actually runs, each with its own
// grading factor, plus a machine flag that keeps machine output off the human
// board. Every org used to be stuck with the same five hardcoded styles
// regardless of whether it ran a wash room, a machine line or hand finishing.
export function WorkTypesTab() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showNew, setShowNew] = useState(false);
  const [label, setLabel] = useState("");
  const [factor, setFactor] = useState("1");
  const [isMachine, setIsMachine] = useState(false);
  const [busy, setBusy] = useState<number | "new" | null>(null);
  const units = useUnits();
  const [unitsBusy, setUnitsBusy] = useState(false);

  const { data: types, isLoading } = useQuery({
    queryKey: ["work-types", "all"],
    queryFn: () => api.get("/api/work-types?include=all").then((r) => readJson<WorkType[]>(r)),
  });

  // Invalidate the bare key too: the worker trim screen reads ["work-types"]
  // for its chips, so an edit here should reach it without a reload.
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["work-types"] });

  async function setUnits(next: UnitSystem) {
    if (next === units || unitsBusy) return;
    setUnitsBusy(true);
    try {
      const res = await api.patchJson("/api/org/settings", { unit_system: next });
      if (!res.ok) { toast(await errText(res, "Couldn't change units"), "err"); return; }
      await queryClient.invalidateQueries({ queryKey: ["org-settings"] });
      toast(next === "imperial" ? "Showing oz / lb" : "Showing grams", "ok");
    } finally {
      setUnitsBusy(false);
    }
  }

  async function errText(res: Response, fallback: string): Promise<string> {
    const body = await res.json().catch(() => ({})) as { error?: string };
    return body.error ?? fallback;
  }

  async function create() {
    const f = Number(factor);
    if (!label.trim()) { toast("Give it a name", "err"); return; }
    if (!Number.isFinite(f) || f <= 0 || f > 10) { toast("Factor must be between 0 and 10", "err"); return; }
    setBusy("new");
    try {
      const res = await api.postJson("/api/work-types", {
        label: label.trim(), speed_factor: f, is_machine: isMachine,
      });
      if (!res.ok) { toast(await errText(res, "Couldn't add that"), "err"); return; }
      setShowNew(false);
      setLabel("");
      setFactor("1");
      setIsMachine(false);
      await refresh();
      toast("Work type added", "ok");
    } finally {
      setBusy(null);
    }
  }

  async function patch(t: WorkType, body: Record<string, unknown>, note: string) {
    setBusy(t.id);
    try {
      const res = await api.patchJson(`/api/work-types/${t.id}`, body);
      if (!res.ok) { toast(await errText(res, "Couldn't save"), "err"); return; }
      await refresh();
      toast(note, "ok");
    } finally {
      setBusy(null);
    }
  }

  async function remove(t: WorkType) {
    setBusy(t.id);
    try {
      const res = await api.del(`/api/work-types/${t.id}`);
      const body = await res.json().catch(() => ({})) as { error?: string; deactivated?: boolean };
      if (!res.ok) { toast(body.error ?? "Couldn't remove", "err"); return; }
      await refresh();
      // A type that historical runs reference is deactivated rather than
      // deleted, so those runs keep the factor they were graded with.
      toast(body.deactivated ? `${t.label} is in use — turned off instead` : `${t.label} removed`, "ok");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="panel">
      <SitesSection />
      <div className="field" style={{ marginBottom: 14 }} data-testid="units-control">
        <div className="field-label">Units</div>
        <div className="chip-grid" role="group" aria-label="Units">
          {(["metric", "imperial"] as const).map((u) => (
            <button
              key={u}
              type="button"
              className={`chip${units === u ? " active" : ""}`}
              aria-pressed={units === u}
              data-testid={`units-${u}`}
              disabled={unitsBusy}
              onClick={() => setUnits(u)}
            >
              {u === "metric" ? "Metric" : "Imperial"}
            </button>
          ))}
        </div>
        <div className="small muted" style={{ marginTop: 6 }}>
          Weights show as grams (g, g/min) or ounces and pounds (oz, lb, oz/hr) for the whole team. Stored data stays in grams.
        </div>
      </div>
      <h2>
        Work types
        <button
          type="button"
          className="btn btn-primary btn-sm"
          style={{ marginLeft: "auto" }}
          data-testid="new-work-type-open"
          onClick={() => setShowNew(true)}
        >
          + New work type
        </button>
      </h2>
      <div className="small muted" style={{ marginBottom: 10 }}>
        The styles your room actually runs. The factor is how much easier the work is than plain
        dry trim — wet is 1.5 because the same grams a minute is less impressive wet. Adjusted
        g/min divides by it, so the board stays fair across styles. Anything a machine produces
        counts toward job totals but stays off the human board.
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr><th>Type</th><th>Factor</th><th>Counts as</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5} className="muted">Loading…</td></tr>}
            {!isLoading && !types?.length && (
              <tr><td colSpan={5} className="muted">No work types yet.</td></tr>
            )}
            {(types || []).map((t) => (
              <tr key={t.id} data-testid={`work-type-${t.slug}`}>
                <td>
                  <b>{t.label}</b>
                  <div className="muted small">{t.slug}{t.builtin ? " · built-in" : ""}</div>
                </td>
                <td>
                  <input
                    type="number"
                    step="0.05"
                    min="0.05"
                    max="10"
                    style={{ width: 90 }}
                    defaultValue={t.speed_factor}
                    disabled={busy === t.id}
                    data-testid={`work-type-factor-${t.slug}`}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isFinite(v) && v !== t.speed_factor) {
                        void patch(t, { speed_factor: v }, `${t.label} factor saved`);
                      }
                    }}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy === t.id}
                    data-testid={`work-type-machine-${t.slug}`}
                    onClick={() => void patch(
                      t,
                      { is_machine: !t.is_machine },
                      t.is_machine ? `${t.label} counts as a person again` : `${t.label} is machine output`,
                    )}
                  >
                    {t.is_machine ? "Machine" : "Person"}
                  </button>
                </td>
                <td>
                  <span className={`pill${t.active ? " live-pill" : ""}`}>{t.active ? "on" : "off"}</span>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy === t.id}
                    data-testid={`work-type-toggle-${t.slug}`}
                    onClick={() => void patch(
                      t,
                      { active: !t.active },
                      t.active ? `${t.label} turned off` : `${t.label} turned on`,
                    )}
                  >
                    {t.active ? "Turn off" : "Turn on"}
                  </button>
                  {!t.builtin && (
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      disabled={busy === t.id}
                      data-testid={`work-type-delete-${t.slug}`}
                      onClick={() => void remove(t)}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && (
        <Modal title="New work type" onClose={() => setShowNew(false)}>
          <div className="field">
            <label className="field-label" htmlFor="wt-label">Name</label>
            <input
              id="wt-label"
              data-testid="new-work-type-label"
              value={label}
              maxLength={40}
              placeholder="Wash material"
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="wt-factor">Speed factor</label>
            <input
              id="wt-factor"
              data-testid="new-work-type-factor"
              type="number"
              step="0.05"
              min="0.05"
              max="10"
              value={factor}
              onChange={(e) => setFactor(e.target.value)}
            />
            <div className="muted small">1.0 = as hard as dry trim. Higher = easier, faster work.</div>
          </div>
          <label className="check-row">
            <input
              type="checkbox"
              data-testid="new-work-type-machine"
              checked={isMachine}
              onChange={(e) => setIsMachine(e.target.checked)}
            />
            <span>Machine output — keep off the human leaderboard</span>
          </label>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
            <button
              type="button"
              className="btn btn-primary"
              data-testid="new-work-type-submit"
              disabled={busy === "new"}
              onClick={() => void create()}
            >
              Create
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
